/**
 * 文档详情页
 *
 * 展示知识库文档完整信息、内容全文、文件属性和各项预置运行指标
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Descriptions,
    Tag,
    Button,
    Spin,
    message,
    Space,
    Divider,
    Row,
    Col,
    Statistic
} from 'antd';
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    SyncOutlined,
    ClockCircleOutlined,
    DatabaseOutlined,
    EyeOutlined,
    EditOutlined
} from '@ant-design/icons';
import { knowledgeApi } from '../../services/api';

const { Title, Text, Paragraph } = Typography;

const statusMap: Record<string, { color: string; label: string }> = {
    draft: { color: 'default', label: '草稿' },
    indexing: { color: 'processing', label: '索引中' },
    indexed: { color: 'cyan', label: '已索引' },
    published: { color: 'green', label: '已发布' },
    archived: { color: 'orange', label: '已归档' },
    failed: { color: 'red', label: '索引失败' },
};

const KnowledgeDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [doc, setDoc] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDetail = async () => {
            if (!id) return;
            try {
                const res = await knowledgeApi.get(Number(id));
                setDoc(res.data);
            } catch (err) {
                message.error('获取文档详情失败');
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [id]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!doc) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Typography.Text type="secondary">文档不存在</Typography.Text>
            </div>
        );
    }

    const formatSize = (bytes: number) => {
        if (!bytes) return '—';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('zh-CN');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fafafa' }}>
            {/* 顶部导航 */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 32px',
                    height: 64,
                    background: '#fff',
                    borderBottom: '1px solid #e4e4e7',
                    flexShrink: 0,
                }}
            >
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/admin/knowledge')}
                    style={{ marginRight: 16 }}
                >
                    返回列表
                </Button>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#18181b' }}>
                    文档详情
                </h2>
            </div>

            {/* 内容区 */}
            <div style={{ flex: 1, padding: '24px 32px', overflow: 'auto' }}>
                <Row gutter={[24, 24]}>
                    <Col span={16}>
                        {/* 左侧：内容展示 */}
                        <Card variant="borderless" style={{ borderRadius: 12, minHeight: '100%' }}>
                            <div style={{ marginBottom: 24 }}>
                                <Title level={3} style={{ marginTop: 0 }}>
                                    {doc.title}
                                </Title>
                                <Space size={16} wrap>
                                    <Tag color={statusMap[doc.status]?.color || 'default'}>
                                        {statusMap[doc.status]?.label || doc.status}
                                    </Tag>
                                    {doc.category && <Tag>{doc.category}</Tag>}
                                    {doc.tags?.map((t: string) => (
                                        <Tag key={t} color="blue">{t}</Tag>
                                    ))}
                                </Space>
                            </div>

                            <Divider />

                            <div style={{
                                background: '#f8fafc',
                                padding: 24,
                                borderRadius: 8,
                                border: '1px solid #e2e8f0',
                                maxHeight: '600px',
                                overflowY: 'auto'
                            }}>
                                {doc.content ? (
                                    <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                                        {doc.content}
                                    </Paragraph>
                                ) : (
                                    <Text type="secondary">文件暂无本文内容提取或内容为空</Text>
                                )}
                            </div>
                        </Card>
                    </Col>

                    <Col span={8}>
                        {/* 右侧：状态统计与统计面板 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {/* 文件信息 */}
                            <Card title="文件与检索信息" variant="borderless" style={{ borderRadius: 12 }}>
                                <Descriptions column={1} size="small" labelStyle={{ color: '#71717a' }}>
                                    <Descriptions.Item label="原文件名">
                                        <Text ellipsis style={{ maxWidth: 200 }} title={doc.file_info?.name}>
                                            {doc.file_info?.name || '—'}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="文件大小">
                                        {formatSize(doc.file_info?.size)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="提取模型">
                                        <Tag icon={<DatabaseOutlined />}>{doc.embedding_model || '—'}</Tag>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            {/* 高级统计指标 */}
                            <Card title="统计指标" variant="borderless" style={{ borderRadius: 12 }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <Statistic title="字符数" value={doc.statistics?.char_count || 0} />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="词汇/分词" value={doc.statistics?.word_count || 0} />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="段落数" value={doc.statistics?.paragraph_count || 0} />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="查阅次数" value={doc.access_count} prefix={<EyeOutlined />} />
                                    </Col>
                                </Row>
                            </Card>

                            {/* 时间线状态 */}
                            <Card title="生命周期时间" variant="borderless" style={{ borderRadius: 12 }}>
                                <Descriptions column={1} size="small" labelStyle={{ color: '#71717a' }}>
                                    <Descriptions.Item label="创建时间">
                                        <Text type="secondary"><ClockCircleOutlined style={{ marginRight: 6 }} />{formatDate(doc.created_at)}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="最新修改">
                                        <Text type="secondary"><EditOutlined style={{ marginRight: 6 }} />{formatDate(doc.updated_at)}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="完成向量">
                                        <Text type="secondary"><CheckCircleOutlined style={{ marginRight: 6 }} />{formatDate(doc.indexed_at)}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="最近访问">
                                        <Text type="secondary"><SyncOutlined style={{ marginRight: 6 }} />{formatDate(doc.last_accessed)}</Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default KnowledgeDetailPage;
